import {
  CommitScopeChoice,
  type ConversationCommitFiles,
  useCommitScope,
} from '@crowdex/host-kit/renderer/commit-scope';
import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { useState } from 'react';
import { readNativeGitGenerationContext } from '@/lib/native-desktop';

type SwitchBranchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branch: string | null;
  error: string | null;
  onConfirm: () => void;
  /** Commits, then switches: `paths` (the conversation's files) only, or (absent) everything. */
  onCommitConfirm: (message: string, paths?: readonly string[]) => void;
  onStashConfirm: () => void;
  working: boolean;
  filesChanged: number;
  additions: number;
  deletions: number;
  /** The checkout, read for what a commit would take. */
  workspace?: string | null;
  /** The files the conversation's turns changed: what the commit takes, unless told otherwise. */
  conversationFiles?: ConversationCommitFiles | null;
};

/**
 * Confirms switching the main checkout while the working tree has uncommitted
 * changes. Plain switch lets Git carry the changes across (or abort on
 * conflict); "Stash & switch" stashes them first and restores them on the
 * target branch. No work is discarded in either path. "Commit & switch" commits the
 * conversation's own changes, or all of them if the user says so, before switching.
 */
export function SwitchBranchDialog({
  open,
  onOpenChange,
  branch,
  error,
  onConfirm,
  onCommitConfirm,
  onStashConfirm,
  working,
  filesChanged,
  additions,
  deletions,
  workspace = null,
  conversationFiles = null,
}: SwitchBranchDialogProps) {
  const [commitMessage, setCommitMessage] = useState('');
  const commitScope = useCommitScope({
    workspace,
    conversationFiles,
    active: open,
    read: readNativeGitGenerationContext,
  });
  const nothingInScope = commitScope.preview !== null && commitScope.preview.files.length === 0;
  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) setCommitMessage('');
      }}
      open={open}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Switch branch with local changes</DialogTitle>
          <DialogDescription>
            You have uncommitted changes. Switching to {branch ?? 'the selected branch'} keeps them
            in your working tree, or stops if they conflict. Nothing is discarded.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 rounded-lg border border-hairline bg-muted/30 px-3 py-2 text-sm">
          <span className="flex-1 text-muted-foreground">
            {filesChanged} changed {filesChanged === 1 ? 'file' : 'files'}
          </span>
          <span className="text-desktop-success tabular-nums">+{additions}</span>
          <span className="text-destructive tabular-nums">−{deletions}</span>
        </div>
        <label className="grid gap-1.5 text-sm" htmlFor="switch-branch-commit-message">
          Commit message
          <Input
            id="switch-branch-commit-message"
            onChange={(event) => setCommitMessage(event.target.value)}
            placeholder="Save work before switching"
            value={commitMessage}
          />
        </label>
        {workspace !== null && <CommitScopeChoice state={commitScope} />}
        {error !== null && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter className="sm:justify-between">
          <Button onClick={() => onOpenChange(false)} variant="ghost">
            Cancel
          </Button>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              disabled={
                working || branch === null || nothingInScope || commitMessage.trim().length === 0
              }
              onClick={() => onCommitConfirm(commitMessage, commitScope.paths)}
              variant="outline"
            >
              Commit & switch
            </Button>
            <Button
              disabled={working || branch === null}
              onClick={() => onStashConfirm()}
              variant="outline"
            >
              Stash & switch
            </Button>
            <Button disabled={working || branch === null} onClick={() => onConfirm()}>
              Switch branch
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
